/* ==========================================================================
   STF DIAMONDS — header.js
   Mega menus, transparent/hide-on-scroll header, mobile drawer, fullscreen
   predictive search, ajax cart drawer. Vanilla JS, no dependencies.
   Loaded (deferred) by stf-header, stf-search-overlay and cart-drawer
   sections — the init guard makes duplicate script tags a no-op.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__stfHeaderInit) return;
  window.__stfHeaderInit = true;

  var doc = document;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function ready(fn) {
    if (doc.readyState === 'loading') {
      doc.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    var header = doc.querySelector('[data-stf-header]');
    var backdrop = doc.querySelector('[data-backdrop]');

    /* ------------------------------------------------------------------
       Overlay manager (data-open / data-close convention).
       base.js ships the canonical manager (shared backdrop, scroll lock,
       focus trap, Escape = one layer). When it is present we DELEGATE to
       window.STF.openOverlay/closeOverlay so a second backdrop/scroll-lock/
       focus trap is never stacked on top of it; the fallback implementation
       below only runs when base.js is missing or failed to load.
       ------------------------------------------------------------------ */
    var lastOpener = null;
    // Checked at use time, not once at load: header.js (deferred, in the
    // header section) runs BEFORE base.js (deferred, end of layout), so a
    // load-time check was always false and both managers handled every
    // panel — leaving the backdrop stuck after closing popups/drawers.
    function baseOverlaysOn() {
      return !!(window.STF &&
        typeof window.STF.openOverlay === 'function' &&
        typeof window.STF.closeOverlay === 'function');
    }

    function openOverlays() {
      return doc.querySelectorAll('[data-overlay].is-open');
    }

    function syncChrome() {
      var anyOpen = openOverlays().length > 0;
      var megaOpen = header && header.classList.contains('is-mega-open');
      // The overlay backdrop + scroll lock belong to base.js when present;
      // the stf backdrop then only serves the mega menus.
      if (backdrop) {
        backdrop.classList.toggle('is-open', (anyOpen && !baseOverlaysOn()) || !!megaOpen);
      }
      doc.body.classList.toggle('stf-lock', anyOpen && !baseOverlaysOn());
      if (header) header.classList.toggle('is-ui-open', anyOpen);
      var burger = doc.querySelector('.stf-header__burger');
      if (burger) {
        var menu = doc.getElementById('MobileMenu');
        burger.setAttribute('aria-expanded', menu && menu.classList.contains('is-open') ? 'true' : 'false');
      }
    }

    function openTarget(selector, opener) {
      var el;
      try {
        el = doc.querySelector(selector);
      } catch (err) {
        el = null;
      }
      if (!el) return false;
      closeMegas();
      if (baseOverlaysOn()) {
        window.STF.openOverlay(el, opener || null);
        syncChrome();
        return true;
      }
      el.classList.add('is-open');
      lastOpener = opener || null;
      syncChrome();
      var focusEl = el.querySelector('[data-autofocus]') ||
        el.querySelector('button, a, input, [tabindex]');
      if (focusEl) {
        window.setTimeout(function () { focusEl.focus(); }, 80);
      }
      return true;
    }

    function closeAll() {
      if (baseOverlaysOn()) {
        openOverlays().forEach(function (el) { window.STF.closeOverlay(el); });
        closeMegas();
        syncChrome();
        return;
      }
      openOverlays().forEach(function (el) { el.classList.remove('is-open'); });
      closeMegas();
      syncChrome();
      if (lastOpener && typeof lastOpener.focus === 'function') lastOpener.focus();
      lastOpener = null;
    }

    if (!window.__stfOverlayManager) {
      window.__stfOverlayManager = true;

      doc.addEventListener('click', function (e) {
        var opener = e.target.closest('[data-open]');
        if (opener) {
          // If the target element exists, open it and cancel navigation.
          // (With base.js present its own listener has already opened the
          // panel; openTarget is then just mega cleanup + chrome sync.)
          // If it does not exist (e.g. concierge modal not rendered), let
          // the href fallback navigate normally.
          if (openTarget(opener.getAttribute('data-open'), opener)) {
            e.preventDefault();
          }
          return;
        }
        var closer = e.target.closest('[data-close], [data-close-on-nav]');
        if (closer) {
          if (baseOverlaysOn()) {
            // base.js closes [data-close] panels itself (one layer). It does
            // not know data-close-on-nav, so close that panel here before
            // the link navigates.
            if (closer.hasAttribute('data-close-on-nav')) {
              var panel = closer.closest('[data-overlay]');
              if (panel) window.STF.closeOverlay(panel);
            }
            closeMegas();
            syncChrome();
            return;
          }
          if (closer.hasAttribute('data-close')) e.preventDefault();
          closeAll();
          return;
        }
        if (backdrop && e.target === backdrop) {
          closeAll();
        }
      });

      doc.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (baseOverlaysOn()) {
          // base.js closes the top overlay (one layer per Escape); the
          // header only needs to clear its mega menus.
          closeMegas();
          syncChrome();
          return;
        }
        closeAll();
      });

      {
        // Keep header chrome (transparent state, burger aria, mega backdrop)
        // in sync however base.js opens/closes a panel (Escape, backdrop
        // click, data-close, programmatic).
        doc.addEventListener('overlay:open', syncChrome);
        doc.addEventListener('overlay:close', syncChrome);
      }
    }

    /* ------------------------------------------------------------------
       Mega menus
       ------------------------------------------------------------------ */
    var megaItems = Array.prototype.slice.call(doc.querySelectorAll('[data-mega-item]'));
    var megaTimer = null;
    var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');

    function closeMegas() {
      megaItems.forEach(function (item) {
        item.classList.remove('is-open');
        var trigger = item.querySelector('[data-mega-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
      if (header) header.classList.remove('is-mega-open');
    }

    function openMega(item) {
      megaItems.forEach(function (other) {
        if (other !== item) {
          other.classList.remove('is-open');
          var t = other.querySelector('[data-mega-trigger]');
          if (t) t.setAttribute('aria-expanded', 'false');
        }
      });
      item.classList.add('is-open');
      var trigger = item.querySelector('[data-mega-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      if (header) {
        header.classList.add('is-mega-open');
        header.classList.remove('is-hidden');
      }
      syncChrome();
    }

    megaItems.forEach(function (item) {
      var trigger = item.querySelector('[data-mega-trigger]');
      if (!trigger) return;

      item.addEventListener('mouseenter', function () {
        if (!hoverCapable.matches) return;
        window.clearTimeout(megaTimer);
        megaTimer = window.setTimeout(function () { openMega(item); }, 60);
      });

      item.addEventListener('mouseleave', function () {
        if (!hoverCapable.matches) return;
        window.clearTimeout(megaTimer);
        megaTimer = window.setTimeout(function () {
          closeMegas();
          syncChrome();
        }, 140);
      });

      trigger.addEventListener('click', function (e) {
        // Category names are real links to the full category page. With a mouse the
        // mega menu is already open from hover, so a click navigates. On touch the
        // first tap opens the menu and a second tap on the open item navigates.
        if (trigger.tagName === 'A' && trigger.getAttribute('href') &&
            (hoverCapable.matches || item.classList.contains('is-open'))) {
          return;
        }
        e.preventDefault();
        if (item.classList.contains('is-open')) {
          closeMegas();
          syncChrome();
        } else {
          openMega(item);
        }
      });
    });

    // Close megas when focus leaves the header (keyboard users)
    doc.addEventListener('focusin', function (e) {
      if (header && header.classList.contains('is-mega-open') && !header.contains(e.target)) {
        closeMegas();
        syncChrome();
      }
    });

    /* ------------------------------------------------------------------
       Scroll behavior: hide on scroll down, reveal on scroll up,
       solidify transparent header after a small scroll.
       ------------------------------------------------------------------ */
    if (header && !header.dataset.scrollBound) {
      header.dataset.scrollBound = 'true';
      var lastY = window.scrollY;

      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        header.classList.toggle('is-scrolled', y > 10);

        var uiOpen = header.classList.contains('is-mega-open') || openOverlays().length > 0;
        if (!uiOpen) {
          if (y > lastY + 4 && y > 200) {
            header.classList.add('is-hidden');
            closeMegas();
            syncChrome();
          } else if (y < lastY - 4 || y <= 200) {
            header.classList.remove('is-hidden');
          }
        }
        lastY = y;
      }, { passive: true });
    }

    /* ------------------------------------------------------------------
       Predictive search (fullscreen overlay)
       ------------------------------------------------------------------ */
    var searchInput = doc.querySelector('[data-search-input]');
    var searchResults = doc.querySelector('[data-search-results]');
    var searchClear = doc.querySelector('[data-search-clear]');
    var searchTimer = null;
    var searchController = null;

    function formatMoney(value) {
      var num = parseFloat(value);
      if (isNaN(num)) return value;
      return '$' + num.toFixed(2).replace(/\.00$/, '');
    }

    function el(tag, className, text) {
      var node = doc.createElement(tag);
      if (className) node.className = className;
      if (text != null) node.textContent = text;
      return node;
    }

    function renderSuggestions(data, query) {
      if (!searchResults) return;
      searchResults.innerHTML = '';
      var resources = (data && data.resources && data.resources.results) || {};
      var products = resources.products || [];
      var collections = resources.collections || [];
      var searchUrl = searchResults.getAttribute('data-search-url') || '/search';

      if (!products.length && !collections.length) {
        searchResults.appendChild(
          el('p', 'search-ov__none', 'No matches yet — try a different word, or browse a popular search above.')
        );
        return;
      }

      if (collections.length) {
        searchResults.appendChild(el('p', 'search-ov__results-title', 'Collections'));
        var cList = el('ul', 'search-ov__collections');
        collections.forEach(function (c) {
          var li = doc.createElement('li');
          var a = el('a', 'search-ov__chip', c.title);
          a.href = c.url;
          li.appendChild(a);
          cList.appendChild(li);
        });
        searchResults.appendChild(cList);
      }

      if (products.length) {
        searchResults.appendChild(el('p', 'search-ov__results-title', 'Products'));
        var pList = el('ul', 'search-ov__products');
        pList.setAttribute('role', 'list');
        products.forEach(function (p) {
          var li = doc.createElement('li');
          var a = el('a', 'search-ov__product');
          a.href = p.url;
          var media = el('span', 'search-ov__product-media');
          if (p.featured_image && p.featured_image.url) {
            var img = doc.createElement('img');
            img.src = p.featured_image.url + (p.featured_image.url.indexOf('?') > -1 ? '&' : '?') + 'width=128';
            img.alt = p.featured_image.alt || p.title;
            img.loading = 'lazy';
            img.width = 64;
            img.height = 64;
            media.appendChild(img);
          }
          var body = el('span');
          body.appendChild(el('p', 'search-ov__product-title', p.title));
          if (p.price != null) {
            body.appendChild(el('p', 'search-ov__product-price', formatMoney(p.price)));
          }
          a.appendChild(media);
          a.appendChild(body);
          li.appendChild(a);
          pList.appendChild(li);
        });
        searchResults.appendChild(pList);

        var viewAll = el('a', 'search-ov__view-all', 'View all results for “' + query + '” →');
        viewAll.href = searchUrl + '?q=' + encodeURIComponent(query) + '&type=product&options%5Bprefix%5D=last';
        searchResults.appendChild(viewAll);
      }
    }

    function runSuggest(query) {
      if (searchController) searchController.abort();
      searchController = new AbortController();
      var url = root() +
        'search/suggest.json?q=' + encodeURIComponent(query) +
        '&resources[type]=product,collection' +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=last';
      fetch(url, { signal: searchController.signal })
        .then(function (r) { return r.json(); })
        .then(function (data) { renderSuggestions(data, query); })
        .catch(function () { /* aborted or offline — keep current results */ });
    }

    if (searchInput && searchResults) {
      searchInput.addEventListener('input', function () {
        var q = searchInput.value.trim();
        if (searchClear) searchClear.hidden = q.length === 0;
        window.clearTimeout(searchTimer);
        if (q.length < 2) {
          searchResults.innerHTML = '';
          return;
        }
        searchTimer = window.setTimeout(function () { runSuggest(q); }, 250);
      });
    }

    if (searchClear && searchInput) {
      searchClear.addEventListener('click', function () {
        searchInput.value = '';
        searchClear.hidden = true;
        if (searchResults) searchResults.innerHTML = '';
        searchInput.focus();
      });
    }

    /* ------------------------------------------------------------------
       Ajax cart drawer
       ------------------------------------------------------------------ */
    function cartDrawerEl() {
      return doc.getElementById('CartDrawer');
    }

    var cartBusy = false;

    function setCartBusy(busy) {
      cartBusy = busy;
      var inner = doc.querySelector('[data-cart-drawer]');
      if (inner) inner.classList.toggle('is-busy', busy);
    }

    function showCartError(message) {
      var el = doc.querySelector('#CartDrawer [data-cart-error]');
      if (!el) return;
      el.textContent = message || 'Could not update your cart. Please try again.';
      el.hidden = false;
    }

    function updateCartCounts(count) {
      doc.querySelectorAll('[data-cart-count]').forEach(function (node) {
        node.textContent = count;
        node.classList.toggle('is-zero', Number(count) === 0);
      });
    }

    function refreshCartDrawer(openAfter) {
      // Remember which line-item control had focus so the innerHTML swap
      // does not dump keyboard/AT users back to <body>.
      var drawer = cartDrawerEl();
      var active = doc.activeElement;
      var focusLine = null;
      var focusSelector = null;
      if (drawer && active && drawer.contains(active)) {
        focusLine = active.getAttribute('data-line');
        if (focusLine) {
          if (active.hasAttribute('data-qty-change')) {
            focusSelector = '[data-qty-change="' + active.getAttribute('data-qty-change') + '"][data-line="' + focusLine + '"]';
          } else if (active.hasAttribute('data-qty-input')) {
            focusSelector = '[data-qty-input][data-line="' + focusLine + '"]';
          } else if (active.hasAttribute('data-remove-item')) {
            focusSelector = '[data-remove-item][data-line="' + focusLine + '"]';
          }
        }
      }

      return fetch(root() + '?section_id=cart-drawer')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('#CartDrawer');
          var current = cartDrawerEl();
          if (fresh && current) {
            current.innerHTML = fresh.innerHTML;
            if (focusSelector) {
              var target = current.querySelector(focusSelector) ||
                current.querySelector('[data-line="' + focusLine + '"] [data-qty-input]') ||
                current.querySelector('[data-qty-input]') ||
                current.querySelector('[data-close]');
              if (target && typeof target.focus === 'function') {
                target.focus({ preventScroll: true });
              }
            }
          }
          var inner = doc.querySelector('[data-cart-drawer]');
          if (inner) {
            updateCartCounts(inner.getAttribute('data-cart-count-value') || '0');
          }
          if (openAfter && current && !current.classList.contains('is-open')) {
            openTarget('#CartDrawer', null);
          }
        })
        .catch(function () { /* network hiccup — leave drawer as-is */ })
        .then(function () { setCartBusy(false); });
    }

    // Inline ATC error display — reuses pdp.js's [data-atc-error] pattern;
    // creates a status element for forms (quick-add etc.) that lack one.
    function atcErrorEl(form) {
      var scope = form.closest('[data-pdp]') || form;
      var el = scope.querySelector('[data-atc-error]');
      if (!el) {
        el = doc.createElement('p');
        el.className = 'form-status form-status--error';
        el.setAttribute('data-atc-error', '');
        el.setAttribute('role', 'alert');
        el.hidden = true;
        form.appendChild(el);
      }
      return el;
    }

    // Intercept every add-to-cart form sitewide
    doc.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
      if (form.hasAttribute('data-no-ajax')) return;
      // Another handler (pdp.js binds directly on the PDP form) has already
      // ajax-submitted this form — never add the same item twice.
      if (e.defaultPrevented) return;
      e.preventDefault();

      var errorEl = atcErrorEl(form);
      errorEl.hidden = true;

      var submitBtn = form.querySelector('[type="submit"], button:not([type])');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-busy');
      }

      fetch(root() + 'cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      })
        .then(function (r) {
          return r.json()
            .catch(function () { return {}; })
            .then(function (json) {
              if (!r.ok) {
                // Cart-level rejection (sold out, quantity limits …) —
                // surface Shopify's message inline, do NOT re-submit.
                var err = new Error(
                  (json && (json.description || json.message)) ||
                  'Something went wrong. Please try again.'
                );
                err.cartError = true;
                throw err;
              }
              return json;
            });
        })
        .then(function () { return refreshCartDrawer(true); })
        .catch(function (err) {
          if (err && err.cartError) {
            errorEl.textContent = err.message;
            errorEl.hidden = false;
          } else {
            // True network throw — fall back to a native submit.
            form.setAttribute('data-no-ajax', '');
            form.submit();
          }
        })
        .then(function () {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-busy');
          }
        });
    });

    function changeLine(line, quantity) {
      // Serialize: line numbers shift after every change, so ignore new
      // requests until the in-flight update + drawer refresh land.
      if (cartBusy) return;
      setCartBusy(true);
      fetch(root() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: quantity })
      })
        .then(function (r) {
          if (r.ok) return r.json();
          return r.json()
            .catch(function () { return {}; })
            .then(function (json) {
              throw new Error(
                (json && (json.description || json.message)) ||
                'Could not update your cart. Please try again.'
              );
            });
        })
        .then(function () { return refreshCartDrawer(false); })
        .catch(function (err) {
          setCartBusy(false);
          showCartError(err && err.message);
        });
    }

    doc.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('[data-qty-change]');
      if (qtyBtn) {
        var line = qtyBtn.getAttribute('data-line');
        var input = doc.querySelector('[data-qty-input][data-line="' + line + '"]');
        var current = input ? parseInt(input.value, 10) || 0 : 0;
        var next = qtyBtn.getAttribute('data-qty-change') === 'inc' ? current + 1 : current - 1;
        if (next < 0) next = 0;
        changeLine(line, next);
        return;
      }
      var removeBtn = e.target.closest('[data-remove-item]');
      if (removeBtn) {
        e.preventDefault();
        changeLine(removeBtn.getAttribute('data-line'), 0);
      }
    });

    doc.addEventListener('change', function (e) {
      var input = e.target.closest('[data-qty-input]');
      if (!input) return;
      var qty = parseInt(input.value, 10);
      if (isNaN(qty) || qty < 0) qty = 0;
      changeLine(input.getAttribute('data-line'), qty);
    });

    // pdp.js adds via its own fetch and only dispatches `cart:refresh`, then
    // opens the drawer. Nothing listened, so the drawer kept its page-load
    // markup ("Your Cart Is Empty" right after a successful add).
    doc.addEventListener('cart:refresh', function () { refreshCartDrawer(false); });

    // Expose a tiny API for other sections (PDP sticky ATC, etc.)
    window.STFCart = {
      refresh: refreshCartDrawer,
      open: function () { openTarget('#CartDrawer', null); }
    };
  });
})();
